import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TOrderManagementStore,
	TOrderListRequest,
	TOrderListResponsePayload,
	TOrderSplitsResponseData,
	TOrderItemsResponseData,
	TSplitItemsSuccessPayload,
	TOrderReturnItemsResponseData,
	TOrderReturnsResponseData,
	TOrderDiscountsResponseData,
	TOrderDiscountBreakdownResponseData,
	TSwissDeclarationsRequest,
	TSwissDeclarationsResponse,
	TDownloadSwissDeclarationRequest,
} from './types';

const initialState: TOrderManagementStore = {
	orderList: null,
	orderListLoading: false,
	orderSplits: null,
	orderSplitsLoading: false,
	orderItems: null,
	orderItemsLoading: false,
	splitItemsMap: {},
	splitItemsLoadingMap: {},
	orderReturns: null,
	orderReturnsLoading: false,
	orderReturnItems: null,
	orderReturnItemsLoading: false,
	orderDiscounts: null,
	orderDiscountsLoading: false,
	orderDiscountBreakdown: null,
	orderDiscountBreakdownLoading: false,
	swissDeclarations: null,
	swissDeclarationsLoading: false,
	swissDeclarationDownloadLoading: false,
};

const orderManagementSlice = createSlice({
	name: 'orderManagement',
	initialState,
	reducers: {
		getOrderListRequest: (state, _action: PayloadAction<TOrderListRequest>) => {
			state.orderListLoading = true;
		},
		getOrderListSuccess: (state, action: PayloadAction<TOrderListResponsePayload>) => {
			state.orderList = action.payload;
			state.orderListLoading = false;
		},
		getOrderListFailure: (state) => {
			state.orderListLoading = false;
		},
		clearOrderList: (state) => {
			state.orderList = null;
		},

		getOrderSplitsRequest: (state, _action: PayloadAction<number>) => {
			state.orderSplitsLoading = true;
		},
		getOrderSplitsSuccess: (state, action: PayloadAction<TOrderSplitsResponseData>) => {
			state.orderSplits = action.payload;
			state.orderSplitsLoading = false;
		},
		getOrderSplitsFailure: (state) => {
			state.orderSplitsLoading = false;
		},
		clearOrderSplits: (state) => {
			state.orderSplits = null;
		},

		getOrderItemsRequest: (state, _action: PayloadAction<number>) => {
			state.orderItemsLoading = true;
		},
		getOrderItemsSuccess: (state, action: PayloadAction<TOrderItemsResponseData>) => {
			state.orderItems = action.payload;
			state.orderItemsLoading = false;
		},
		getOrderItemsFailure: (state) => {
			state.orderItemsLoading = false;
		},
		clearOrderItems: (state) => {
			state.orderItems = null;
		},

		getOrderReturnsRequest: (state, _action: PayloadAction<TOrderListRequest>) => {
			state.orderReturnsLoading = true;
		},
		getOrderReturnsSuccess: (state, action: PayloadAction<TOrderReturnsResponseData>) => {
			state.orderReturns = action.payload;
			state.orderReturnsLoading = false;
		},
		getOrderReturnsFailure: (state) => {
			state.orderReturnsLoading = false;
		},
		clearOrderReturns: (state) => {
			state.orderReturns = null;
		},

		getOrderReturnItemsRequest: (state, _action: PayloadAction<TOrderListRequest>) => {
			state.orderReturnItemsLoading = true;
		},
		getOrderReturnItemsSuccess: (state, action: PayloadAction<TOrderReturnItemsResponseData>) => {
			state.orderReturnItems = action.payload;
			state.orderReturnItemsLoading = false;
		},
		getOrderReturnItemsFailure: (state) => {
			state.orderReturnItemsLoading = false;
		},
		clearOrderReturnItems: (state) => {
			state.orderReturnItems = null;
		},

		getOrderDiscountsRequest: (state, _action: PayloadAction<TOrderListRequest>) => {
			state.orderDiscountsLoading = true;
		},
		getOrderDiscountsSuccess: (state, action: PayloadAction<TOrderDiscountsResponseData>) => {
			state.orderDiscounts = action.payload;
			state.orderDiscountsLoading = false;
		},
		getOrderDiscountsFailure: (state) => {
			state.orderDiscountsLoading = false;
		},
		clearOrderDiscounts: (state) => {
			state.orderDiscounts = null;
		},

		getOrderDiscountBreakdownRequest: (state, _action: PayloadAction<TOrderListRequest>) => {
			state.orderDiscountBreakdownLoading = true;
		},
		getOrderDiscountBreakdownSuccess: (
			state,
			action: PayloadAction<TOrderDiscountBreakdownResponseData>,
		) => {
			state.orderDiscountBreakdown = action.payload;
			state.orderDiscountBreakdownLoading = false;
		},
		getOrderDiscountBreakdownFailure: (state) => {
			state.orderDiscountBreakdownLoading = false;
		},
		clearOrderDiscountBreakdown: (state) => {
			state.orderDiscountBreakdown = null;
		},

		getSplitItemsBySplitIdRequest: (state, action: PayloadAction<string>) => {
			state.splitItemsLoadingMap[action.payload] = true;
		},
		getSplitItemsBySplitIdSuccess: (state, action: PayloadAction<TSplitItemsSuccessPayload>) => {
			state.splitItemsMap[action.payload.splitId] = action.payload.data;
			state.splitItemsLoadingMap[action.payload.splitId] = false;
		},
		getSplitItemsBySplitIdFailure: (state, action: PayloadAction<string>) => {
			state.splitItemsLoadingMap[action.payload] = false;
		},
		clearSplitItemsMap: (state) => {
			state.splitItemsMap = {};
			state.splitItemsLoadingMap = {};
		},

		getSwissDeclarationsRequest: (state, _action: PayloadAction<TSwissDeclarationsRequest>) => {
			state.swissDeclarationsLoading = true;
		},
		getSwissDeclarationsSuccess: (state, action: PayloadAction<TSwissDeclarationsResponse>) => {
			state.swissDeclarations = action.payload;
			state.swissDeclarationsLoading = false;
		},
		getSwissDeclarationsFailure: (state) => {
			state.swissDeclarationsLoading = false;
		},
		clearSwissDeclarations: (state) => {
			state.swissDeclarations = null;
			state.swissDeclarationsLoading = false;
		},

		downloadSwissDeclarationRequest: (
			state,
			_action: PayloadAction<TDownloadSwissDeclarationRequest>,
		) => {
			state.swissDeclarationDownloadLoading = true;
		},
		downloadSwissDeclarationSuccess: (state) => {
			state.swissDeclarationDownloadLoading = false;
		},
		downloadSwissDeclarationFailure: (state) => {
			state.swissDeclarationDownloadLoading = false;
		},
	},
});

export const {
	getOrderListRequest,
	getOrderListSuccess,
	getOrderListFailure,
	clearOrderList,
	getOrderSplitsRequest,
	getOrderSplitsSuccess,
	getOrderSplitsFailure,
	clearOrderSplits,
	getOrderItemsRequest,
	getOrderItemsSuccess,
	getOrderItemsFailure,
	clearOrderItems,
	getOrderReturnsRequest,
	getOrderReturnsSuccess,
	getOrderReturnsFailure,
	clearOrderReturns,
	getOrderReturnItemsRequest,
	getOrderReturnItemsSuccess,
	getOrderReturnItemsFailure,
	clearOrderReturnItems,
	getOrderDiscountsRequest,
	getOrderDiscountsSuccess,
	getOrderDiscountsFailure,
	clearOrderDiscounts,
	getOrderDiscountBreakdownRequest,
	getOrderDiscountBreakdownSuccess,
	getOrderDiscountBreakdownFailure,
	clearOrderDiscountBreakdown,
	getSplitItemsBySplitIdRequest,
	getSplitItemsBySplitIdSuccess,
	getSplitItemsBySplitIdFailure,
	clearSplitItemsMap,
	getSwissDeclarationsRequest,
	getSwissDeclarationsSuccess,
	getSwissDeclarationsFailure,
	clearSwissDeclarations,
	downloadSwissDeclarationRequest,
	downloadSwissDeclarationSuccess,
	downloadSwissDeclarationFailure,
} = orderManagementSlice.actions;

export default orderManagementSlice.reducer;
